import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';
import type { PersistConfig } from 'redux-persist';

/**
 * Reconciliador de `redux-persist` que ignora los trozos que no se pudieron leer.
 *
 * El transform (`transform-persists.ts`) devuelve `null` cuando un trozo
 * persistido no se puede descomprimir o parsear, y `autoMergeLevel1` asignaba
 * ese `null` tal cual: con `roidOTSelected` a `null`, `Breadcrumbs` leía `.id`
 * y la ruta caía en la página de error (Sentry ROIS-FRONT-C8, 2026-10-08).
 *
 * Aquí se descartan las claves con `null`/`undefined` ANTES de fusionar, así
 * que ese trozo se queda con el estado inicial de su reducer. Ningún reducer
 * persistido arranca en `null` (todos son objetos), de modo que no se pierde
 * nada legítimo: lo persistido es preferencia de interfaz, y perderla es una
 * molestia, no una pantalla rota.
 */
export function reconcileReadable<S extends object>(
  inboundState: Partial<S> | null | undefined,
  originalState: S,
  reducedState: S,
  config: PersistConfig<S>
): S {
  const readable =
    inboundState && typeof inboundState === 'object'
      ? (Object.fromEntries(
          Object.entries(inboundState).filter(
            ([, value]) => value !== null && value !== undefined
          )
        ) as Partial<S>)
      : inboundState;

  return autoMergeLevel1(readable as S, originalState, reducedState, config);
}
